import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../config';

// Send Register OTP async thunk
export const sendRegisterOtp = createAsyncThunk(
  'auth/sendRegisterOtp',
  async ({ phone }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL, {
        query: `
          mutation {
            requestRegisterOTPByPhone(phone: "${phone}") {
              success
              message
              expiresIn
            }
          }
        `
      });

      if (response.data.errors) {
        const errorMessage = response.data.errors[0].message;
        const errorCode = response.data.errors[0].extensions?.code;
        
        // Handle specific error types with proper codes
        if (errorCode === 'RATE_LIMIT_EXCEEDED' || errorMessage.includes('rate limit') || errorMessage.includes('Too many')) {
          return rejectWithValue('RATE_LIMIT_EXCEEDED:' + errorMessage);
        }
        
        return rejectWithValue(errorMessage);
      }

      const { success, message, expiresIn } = response.data.data.requestRegisterOTPByPhone;
      
      if (!success) {
        return rejectWithValue(message || 'Failed to send OTP');
      }
      
      return { success, message, expiresIn };
    } catch (error) {
      if (error.response?.status === 429) {
        return rejectWithValue('RATE_LIMIT_EXCEEDED:Too many requests. Please wait before trying again.');
      }
      return rejectWithValue(error.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

// Register User async thunk
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL, {
        query: `
          mutation {
            verifyRegisterOTP(
              input: {
                firstName: "${userData.firstName}"
                lastName: "${userData.lastName}"
                email: "${userData.email}"
                phone: "${userData.phone}"
                ownerName: "${userData.ownerName}"
                companyName: "${userData.companyName}"
                businessIndustry: "${userData.businessIndustry}"
                city: "${userData.city}"
                state: "${userData.state}"
                country: "${userData.country}"
              }
              otp: "${userData.otp}"
            ) {
              success
              message
              token
              user {
                id
                firstName
                lastName
                email
                phone
                mobile
                ownerName
                companyName
                businessIndustry
                role
                isActive
                email_verified
                email_verified_at
                address_line_1
                address_line_2
                city
                state
                country
                zip_code
                profile_image
                created_at
                updated_at
              }
            }
          }
        `
      });

      if (response.data.errors) {
        return rejectWithValue(response.data.errors[0].message);
      }

      const { success, token, user, message } = response.data.data.verifyRegisterOTP;
      
      if (!success) {
        return rejectWithValue(message || 'Registration failed');
      }

      // Store token in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      return { token, user, message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

// Send OTP async thunk
export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async ({ mobile }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL, {
        query: `
          mutation {
            requestLoginOTP(phone: "${mobile}") {
              success
              message
              expiresIn
            }
          }
        `
      });

      if (response.data.errors) {
        const errorMessage = response.data.errors[0].message;
        const errorCode = response.data.errors[0].extensions?.code;
        
        // Handle user not registered - redirect to registration
        if (errorCode === 'USER_NOT_REGISTERED' || errorMessage.includes('USER_NOT_REGISTERED') || errorMessage.includes('Please register first')) {
          return rejectWithValue('USER_NOT_REGISTERED:' + errorMessage);
        }
        
        // Handle specific error types with proper codes
        if (errorCode === 'RATE_LIMIT_EXCEEDED' || errorMessage.includes('rate limit') || errorMessage.includes('Too many')) {
          return rejectWithValue('RATE_LIMIT_EXCEEDED:' + errorMessage);
        }
        
        return rejectWithValue(errorMessage);
      }

      const { success, message, expiresIn } = response.data.data.requestLoginOTP;
      
      if (!success) {
        return rejectWithValue(message || 'Failed to send OTP');
      }
      
      return { success, message, expiresIn };
    } catch (error) {
      if (error.response?.status === 429) {
        return rejectWithValue('RATE_LIMIT_EXCEEDED:Too many requests. Please wait before trying again.');
      }
      return rejectWithValue(error.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

// Login async thunk
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ mobile, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL, {
        query: `
          mutation {
            verifyLoginOTP(phone: "${mobile}", otp: "${otp}") {
              success
              message
              token
              user {
                id
                firstName
                lastName
                email
                phone
                mobile
                ownerName
                companyName
                businessIndustry
                role
                isActive
                email_verified
                email_verified_at
                address_line_1
                address_line_2
                city
                state
                country
                zip_code
                profile_image
                created_at
                updated_at
              }
            }
          }
        `
      });

      if (response.data.errors) {
        return rejectWithValue(response.data.errors[0].message);
      }

      const { success, token, user, message } = response.data.data.verifyLoginOTP;
      
      if (!success) {
        return rejectWithValue(message || 'Login failed');
      }

      // Store token in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      return { token, user, message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    isLoading: false,
    otpLoading: false,
    error: null,
    message: null,
    isAuthenticated: !!localStorage.getItem('token'),
    rateLimitError: false,
    needsRegistration: false,
  },
  reducers: {
    logout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.message = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
      state.rateLimitError = false;
      state.needsRegistration = false;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    updateUser: (state, action) => {
      // Create a completely new user object to ensure React re-renders
      state.user = { ...state.user, ...action.payload };
      // Update localStorage with the new user data
      localStorage.setItem('user', JSON.stringify(state.user));
    },
  },
  extraReducers: (builder) => {
    builder
      // Send Register OTP cases
      .addCase(sendRegisterOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
        state.rateLimitError = false;
      })
      .addCase(sendRegisterOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.message = action.payload.message;
        state.error = null;
        state.rateLimitError = false;
      })
      .addCase(sendRegisterOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
        
        // Check if it's a rate limit error
        if (action.payload && action.payload.startsWith('RATE_LIMIT_EXCEEDED:')) {
          state.rateLimitError = true;
          state.error = action.payload.replace('RATE_LIMIT_EXCEEDED:', '');
        }
      })
      // Register User cases
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.message = action.payload.message;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Send OTP cases
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
        state.rateLimitError = false;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.message = action.payload.message;
        state.error = null;
        state.rateLimitError = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
        
        // Check if it's a rate limit error
        if (action.payload && action.payload.startsWith('RATE_LIMIT_EXCEEDED:')) {
          state.rateLimitError = true;
          state.error = action.payload.replace('RATE_LIMIT_EXCEEDED:', '');
        }
        
        // Check if user needs to register
        if (action.payload && action.payload.startsWith('USER_NOT_REGISTERED:')) {
          state.needsRegistration = true;
          state.error = action.payload.replace('USER_NOT_REGISTERED:', '');
        }
      })
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.message = action.payload.message;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });
  },
});

export const { logout, clearError, clearMessage, updateUser } = authSlice.actions;
export default authSlice.reducer;